/**
 * KanzleiOnboardingDialog — §5-Kanzlei-Onboarding-Bestätigung (B4,
 * architecture-stb-onboarding-consent.md §6).
 *
 * Modaler Inline-Dialog, der VOR einer Kanzlei-Aktion gezeigt wird, wenn die eigene
 * §5-Onboarding-Bestätigung fehlt/veraltet ist (Accept-Vorprüfung in
 * {@link PendingInvitesPanel}/{@link AdvisorInviteAcceptPanel} bzw. Re-Confirm im
 * Advisor-Kontext). Zeigt ZWEI operator-verfasste, per Fetch geladene Legal-Texte:
 * (1) die versionierte §5-Onboarding-Erklärung (`{{avv_version}}` interpoliert) und
 * (2) — jurist-R1 — den VOLLSTÄNDIGEN AVV-Vertragstext nach Art. 28 DSGVO
 * ({@link STB_AVV_DOC_PATH}), damit die Kanzlei ihn EINSEHEN kann, BEVOR sie per Haken
 * zustimmt (`confirmKanzleiOnboarding` setzt server-seitig `avvAcceptedAt`). Der eine
 * unvorbelegte Haken deckt BEIDES ab (Onboarding-Erklärung UND AVV). Erst danach ruft
 * der Dialog die ENTKOPPELTE, ungated Callable {@link confirmKanzleiOnboarding} (kein
 * Feature-/Beta-Gate — §6.2 Deadlock-Vermeidung) und bei Erfolg `onConfirmed()`.
 *
 * Fail-safe: „Kanzlei-Zugang aktivieren" bleibt gesperrt, solange EINES der beiden
 * Dokumente (§5-Onboarding ODER AVV) fehlt oder den Draft-Marker trägt (Inertheit bis
 * die freigegebenen Dateien vorliegen).
 *
 * @module components/tenant/KanzleiOnboardingDialog
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconWarning } from '../../icons';
import logger from '../../utils/logger';
import SimpleMarkdown from '../inbound/SimpleMarkdown';
import {
  KANZLEI_ONBOARDING_DOC_PATH,
  STB_AVV_DOC_PATH,
  CLIENT_INVITE_AVV_VERSION,
  LEGAL_DRAFT_MARKER,
} from '../../config/advisorConsentAccess';
import { confirmKanzleiOnboarding } from '../../api/clientInviteApi';
import {
  useLegalDocument,
  interpolatePlaceholder,
  type LegalDocState,
} from './useLegalDocument';

interface KanzleiOnboardingDialogProps {
  /** Optionaler Kanzlei-Name, mit dem ein noch fehlendes Profil seed-angelegt wird. */
  kanzleiName?: string | null;
  /** Aufgerufen NACH erfolgreicher §5-Bestätigung (Server-Callable ok). */
  onConfirmed: () => void;
  /** Aufgerufen bei Abbruch/Schließen. */
  onCancel: () => void;
}

/**
 * §5-Onboarding-Modal. Bestätigt server-seitig via {@link confirmKanzleiOnboarding}
 * und ruft danach {@link KanzleiOnboardingDialogProps.onConfirmed}.
 */
const KanzleiOnboardingDialog: React.FC<KanzleiOnboardingDialogProps> = ({
  kanzleiName = null,
  onConfirmed,
  onCancel,
}) => {
  const { t, i18n } = useTranslation('common');
  const [accepted, setAccepted] = useState<boolean>(false); // UNVORBELEGT
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Zwei operator-verfasste Legal-Dokumente: §5-Onboarding-Erklärung + voller
  // AVV-Vertragstext (jurist-R1 — muss VOR dem Haken einsehbar sein).
  const onboardingDoc = useLegalDocument(KANZLEI_ONBOARDING_DOC_PATH, LEGAL_DRAFT_MARKER);
  const avvDoc = useLegalDocument(STB_AVV_DOC_PATH, LEGAL_DRAFT_MARKER);

  const onboardingReady = onboardingDoc.kind === 'ready';
  const avvReady = avvDoc.kind === 'ready';
  // Aktivierung nur, wenn BEIDE Dokumente freigegeben (ready) sind — fail-safe.
  const bothReady = onboardingReady && avvReady;
  const anyBlocked =
    onboardingDoc.kind === 'missing' ||
    onboardingDoc.kind === 'draft' ||
    avvDoc.kind === 'missing' ||
    avvDoc.kind === 'draft';
  const anyDraft = onboardingDoc.kind === 'draft' || avvDoc.kind === 'draft';
  const canActivate = bothReady && accepted && !submitting;
  const isGerman = i18n.language?.toLowerCase().startsWith('de') ?? false;

  // §5-Text mit `{{avv_version}}`-Interpolation (Wortlaut-Treue, §5.6). Der AVV-Text
  // trägt die Version als Literal und wird 1:1 gerendert (kein Platzhalter).
  const renderedOnboardingText =
    onboardingDoc.kind === 'ready' || onboardingDoc.kind === 'draft'
      ? interpolatePlaceholder(
          onboardingDoc.text,
          'avv_version',
          CLIENT_INVITE_AVV_VERSION,
          CLIENT_INVITE_AVV_VERSION,
        )
      : '';
  const renderedAvvText =
    avvDoc.kind === 'ready' || avvDoc.kind === 'draft' ? avvDoc.text : '';

  /** Rendert den Body eines Legal-Doc-Blocks (Text / Lade- / Nicht-verfügbar-Zustand). */
  const renderDocBody = (docState: LegalDocState, text: string): React.ReactNode => {
    if (docState.kind === 'ready' || docState.kind === 'draft') {
      return <SimpleMarkdown text={text} />;
    }
    if (docState.kind === 'loading') return t('kanzleiOnboarding.loading');
    return t('kanzleiOnboarding.notReleased');
  };

  const handleActivate = async () => {
    if (!canActivate) return;
    setSubmitting(true);
    setError(null);
    try {
      await confirmKanzleiOnboarding(kanzleiName ?? undefined);
      onConfirmed();
    } catch (err) {
      logger.error('[KanzleiOnboardingDialog] confirm failed', err);
      setError(t('kanzleiOnboarding.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kanzlei-onboarding-title"
      data-testid="kanzlei-onboarding-dialog"
    >
      <div className="w-full max-w-lg rounded-xl border border-hairline bg-surface-raised p-6 shadow-2xl">
        <h2
          id="kanzlei-onboarding-title"
          className="text-lg font-semibold text-primary"
        >
          {t('kanzleiOnboarding.title')}
        </h2>
        <p className="mt-1 text-sm text-muted">{t('kanzleiOnboarding.intro')}</p>

        {!isGerman && (onboardingReady || avvReady) && (
          <p className="mt-3 rounded-md bg-surface-inset px-3 py-2 text-xs text-secondary">
            {t('kanzleiOnboarding.deOnlyNotice')}
          </p>
        )}

        {/* (1) §5-Onboarding-Erklärung */}
        <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-secondary">
          {t('kanzleiOnboarding.onboardingHeading')}
        </p>
        <div
          className="mt-1 max-h-48 overflow-y-auto rounded-md border border-hairline bg-[rgb(var(--surface-base-rgb)/0.6)] p-3 text-xs text-secondary"
          data-testid="kanzlei-onboarding-doc"
        >
          {renderDocBody(onboardingDoc, renderedOnboardingText)}
        </div>

        {/* (2) Voller AVV-Vertragstext (Art. 28 DSGVO) — jurist-R1: einsehbar VOR dem Haken */}
        <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-secondary">
          {t('kanzleiOnboarding.avvHeading')}
        </p>
        <div
          className="mt-1 max-h-56 overflow-y-auto rounded-md border border-hairline bg-[rgb(var(--surface-base-rgb)/0.6)] p-3 text-xs text-secondary"
          data-testid="kanzlei-avv-doc"
        >
          {renderDocBody(avvDoc, renderedAvvText)}
        </div>

        {anyBlocked && (
          <div
            className="mt-3 flex items-start gap-2 rounded-md bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] p-3"
            role="status"
            data-testid="kanzlei-onboarding-blocked"
          >
            <IconWarning className="mt-0.5 text-[var(--tone-warning-fg)]" />
            <p className="text-sm text-[var(--tone-warning-fg)]">
              {anyDraft
                ? t('kanzleiOnboarding.draft')
                : t('kanzleiOnboarding.notReleased')}
            </p>
          </div>
        )}

        <label className="mt-4 flex items-start gap-2 text-sm text-secondary">
          <input
            type="checkbox"
            checked={accepted}
            disabled={!bothReady || submitting}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-strong bg-surface text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
            data-testid="kanzlei-onboarding-checkbox"
          />
          <span>
            {interpolatePlaceholder(
              t('kanzleiOnboarding.checkboxLabel'),
              'avv_version',
              CLIENT_INVITE_AVV_VERSION,
              CLIENT_INVITE_AVV_VERSION,
            )}
          </span>
        </label>

        {error && (
          <p
            className="mt-3 text-sm text-[var(--tone-danger-fg)]"
            data-testid="kanzlei-onboarding-error"
          >
            {error}
          </p>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="inline-flex items-center rounded-md bg-surface-inset px-3 py-1.5 text-sm font-medium text-secondary hover:bg-surface-hover disabled:opacity-50"
            data-testid="kanzlei-onboarding-cancel"
          >
            {t('kanzleiOnboarding.cancel')}
          </button>
          <button
            type="button"
            onClick={handleActivate}
            disabled={!canActivate}
            className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="kanzlei-onboarding-activate"
          >
            {submitting
              ? t('kanzleiOnboarding.activating')
              : t('kanzleiOnboarding.activateButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KanzleiOnboardingDialog;
